/**
 * Contenu endgame caché (déblocage Paldea champion). Pas de libellé “tour” en UI joueur.
 */

import { TYPES, getTypeInfo } from '~/data/types'
import type { PokemonType } from '~/data/types'
import { getPokemonTypes, getTypeEffectiveness } from '~/data/types'
import { GENERATIONS, getZone } from '~/data/zones'
import type { OwnedPokemon } from '~/stores/useInventoryStore'

const MAX_STARS = 5
import type { BossTrainer, WildPokemon, Zone } from '~/data/zones'

/** Boss tour : 30 s — HP cible (spec joueur) */
export const BATTLE_TOWER_BOSS_HP = 5_759_999

export {
  BATTLE_TOWER_ASCENSION_HP_STEP,
  BATTLE_TOWER_ASCENSION_POWER_CAP,
  getBattleTowerBossHpMult,
  getBattleTowerBossRequiredDps,
  scaleBattleTowerTrialBossHp,
} from '../../../shared/ascension_balance'

/**
 * Épreuves zones 1–18 : seul un Pokémon au moins super-efficace (×2 ou plus sur le boss)
 * contribue au DPS. Les autres slots ne « tankent » pas le check en puissance brute neutre.
 */
export function battleTowerTrialOffenseTypeMult(
  bestTypeMult: number,
  towerZoneIndex: number | null | undefined,
): number {
  // Zones 1–18 : SE obligatoire. Capstone (19) et farm (20) : DPS réel sans filtre SE.
  if (towerZoneIndex == null || towerZoneIndex < 1 || towerZoneIndex > 18) return bestTypeMult
  return bestTypeMult >= 2 ? bestTypeMult : 0
}

/** Sanctuaire : sauvages fragiles pour one-shot + or massif */
export const BATTLE_TOWER_FARM_WILD_HP = 20_000

/** Ordre des boss (1 = zone 1 … 18 = zone 18), zone 19 = Red, zone 20 = farm */
export const BATTLE_TOWER_TYPE_ORDER: PokemonType[] = TYPES.map((t) => t.id)

export const BATTLE_TOWER_CAPSTONE_ZONE_INDEX = 19
export const BATTLE_TOWER_FARM_ZONE_INDEX = 20
export const BATTLE_TOWER_CAPSTONE_BOSS_HP = Math.round(BATTLE_TOWER_BOSS_HP * 1.15)
export const BATTLE_TOWER_CAPSTONE_TIMER_SECONDS = 45

const RED_CAPSTONE_TEAM: BossTrainer['team'] = [
  { nameFr: 'Pikachu', nameEn: 'Pikachu', slug: 'pikachu', level: 100 },
  { nameFr: 'Lokhlass', nameEn: 'Lapras', slug: 'lapras', level: 100 },
  { nameFr: 'Ronflex', nameEn: 'Snorlax', slug: 'snorlax', level: 100 },
  { nameFr: 'Florizarre', nameEn: 'Venusaur', slug: 'venusaur', level: 100 },
  { nameFr: 'Dracaufeu', nameEn: 'Charizard', slug: 'charizard', level: 100 },
  { nameFr: 'Tortank', nameEn: 'Blastoise', slug: 'blastoise', level: 100 },
]

/** Clé progression Paldea (champion) */
export const PALDEA_CHAMPION_BOSS_KEYS = ['g9z13', 'geeta'] as const

const FRONTIER_STAGES_PER_ZONE = 10

/**
 * Stage affiché en UI frontière : après victoire boss, `currentStage` repasse à 1
 * pour éviter un re-combat — l’UI doit quand même montrer 10/10.
 */
export function getFrontierDisplayStage(
  currentGeneration: number,
  currentZone: number,
  currentStage: number,
  defeatedBosses: string[],
): number {
  const gen = GENERATIONS.find((g) => g.id === currentGeneration)
  if (!gen) return Math.max(1, Math.min(FRONTIER_STAGES_PER_ZONE, currentStage))
  const bossKey = `g${currentGeneration}z${currentZone}`
  if (defeatedBosses.includes(bossKey)) return FRONTIER_STAGES_PER_ZONE
  const lastZoneId = gen.zones[gen.zones.length - 1]?.id
  if (
    currentGeneration === 9 &&
    lastZoneId !== undefined &&
    currentZone === lastZoneId &&
    isBattleTowerUnlocked(defeatedBosses)
  ) {
    return FRONTIER_STAGES_PER_ZONE
  }
  return Math.max(1, Math.min(FRONTIER_STAGES_PER_ZONE, currentStage))
}

export function isBattleTowerUnlocked(defeatedBosses: string[]): boolean {
  return PALDEA_CHAMPION_BOSS_KEYS.some((k) => defeatedBosses.includes(k))
}

/** Champion Paldea vaincu : la frontière z13 ne doit plus boucler en étapes 1–10 sauvages. */
export function isPaldeaEndgameFrontierSuperseded(
  currentGeneration: number,
  currentZone: number,
  defeatedBosses: string[],
  battleTowerZone: number | null,
  combatGeneration: number | null,
): boolean {
  if (battleTowerZone != null || combatGeneration != null) return false
  if (!isBattleTowerUnlocked(defeatedBosses)) return false
  if (currentGeneration !== 9) return false
  const gen = GENERATIONS.find((g) => g.id === 9)
  const lastZoneId = gen?.zones[gen.zones.length - 1]?.id
  return lastZoneId !== undefined && currentZone === lastZoneId
}

/** Prochaine étape : 1→18, puis Red (19), puis farm (20). */
export function getNextBattleTowerZoneIndex(
  defeatedTypes: PokemonType[],
  capstoneDefeated = false,
): number {
  const n = defeatedTypes.length
  if (n < BATTLE_TOWER_TYPE_ORDER.length) return n + 1
  if (!capstoneDefeated) return BATTLE_TOWER_CAPSTONE_ZONE_INDEX
  return BATTLE_TOWER_FARM_ZONE_INDEX
}

/** Super efficace (facteur ≥ 2 sur le produit des types défenseur) */
export function hasSuperEffectiveAgainst(poke: OwnedPokemon, defenderTypes: PokemonType[]): boolean {
  const atkTypes = getPokemonTypes(poke)
  let best = 0
  for (const atk of atkTypes) {
    best = Math.max(best, getTypeEffectiveness(atk, defenderTypes))
  }
  return best >= 2
}

export function isBattleTowerTeamValid(bossTypes: PokemonType[], team: OwnedPokemon[]): boolean {
  if (team.length !== 6) return false
  for (const poke of team) {
    if (poke.rarity !== 'legendary' || !poke.isShiny || poke.stars < MAX_STARS) return false
    if (!hasSuperEffectiveAgainst(poke, bossTypes)) return false
  }
  return true
}

/**
 * Sprites dresseurs Showdown : `sprites/trainers/{slug}.png`
 * Un champion d’arène / spécialiste par type (HGSS–XY), cohérent visuellement avec le type de l’épreuve.
 */
export const BATTLE_TOWER_TRAINER_SLUGS: Record<PokemonType, string> = {
  normal: 'whitney',
  fire: 'blaine',
  water: 'misty',
  electric: 'elesa',
  grass: 'erika',
  ice: 'brycen',
  fighting: 'brawly',
  poison: 'koga',
  ground: 'clay',
  flying: 'winona',
  psychic: 'sabrina',
  bug: 'bugsy',
  rock: 'brock',
  ghost: 'morty',
  dragon: 'clair',
  dark: 'karen',
  steel: 'jasmine',
  fairy: 'valerie',
}

function bossMeta(type: PokemonType): { nameFr: string; nameEn: string; slug: string } {
  const info = TYPES.find((t) => t.id === type)!
  return {
    nameFr: `Maître ${info.nameFr}`,
    nameEn: `${info.nameEn} master`,
    slug: BATTLE_TOWER_TRAINER_SLUGS[type],
  }
}

const wildPoolByType: Partial<Record<PokemonType, WildPokemon[]>> = {}

/** Sauvages Paldea (toutes zones) dont au moins un type offensif correspond. */
export function getBattleTowerWildPoolForType(t: PokemonType): WildPokemon[] {
  if (wildPoolByType[t]) return wildPoolByType[t]!
  const bySlug = new Map<string, WildPokemon>()
  for (let z = 1; z <= 13; z++) {
    const zone = getZone(9, z)
    if (!zone) continue
    for (const w of zone.wild) {
      if (getPokemonTypes(w.slug).includes(t) && !bySlug.has(w.slug)) {
        bySlug.set(w.slug, w)
      }
    }
  }
  const list = [...bySlug.values()]
  wildPoolByType[t] =
    list.length > 0
      ? list
      : [
          {
            nameFr: 'Rattata',
            nameEn: 'Rattata',
            slug: 'rattata',
            type: 'normal',
            baseHp: 30,
            baseAtk: 6,
          },
        ]
  return wildPoolByType[t]!
}

function bossTeamPreview(type: PokemonType): BossTrainer['team'] {
  const pool = getBattleTowerWildPoolForType(type)
  const team: BossTrainer['team'] = []
  for (let i = 0; i < 6; i++) {
    const p = pool[i % pool.length]!
    team.push({ nameFr: p.nameFr, nameEn: p.nameEn, slug: p.slug, level: 100 })
  }
  return team
}

/** Zone 1–18 : boss mono-type uniquement (pas de sauvages ; équipe d’affichage issue du pool type) */
export function getBattleTowerBossZone(zoneIndex: number): Zone | undefined {
  if (zoneIndex < 1 || zoneIndex > 18) return undefined
  const type = BATTLE_TOWER_TYPE_ORDER[zoneIndex - 1]!
  const meta = bossMeta(type)
  const boss: BossTrainer = {
    nameFr: meta.nameFr,
    nameEn: meta.nameEn,
    slug: meta.slug,
    types: [type],
    team: bossTeamPreview(type),
    timerSeconds: 30,
  }
  return {
    id: zoneIndex,
    nameFr: `Épreuve — ${meta.nameFr}`,
    nameEn: `Trial — ${meta.nameEn}`,
    types: [type],
    wild: [],
    boss,
  }
}

/** Zone 19 — Red (capstone fin de tour) */
export function getBattleTowerCapstoneZone(): Zone {
  const boss: BossTrainer = {
    nameFr: 'Red',
    nameEn: 'Red',
    slug: 'red',
    types: ['fire', 'water', 'electric', 'grass', 'normal'],
    team: RED_CAPSTONE_TEAM,
    timerSeconds: BATTLE_TOWER_CAPSTONE_TIMER_SECONDS,
  }
  return {
    id: BATTLE_TOWER_CAPSTONE_ZONE_INDEX,
    nameFr: 'Épreuve finale — Red',
    nameEn: 'Final trial — Red',
    types: boss.types ?? ['normal'],
    wild: [],
    boss,
  }
}

/** Farm légendaires (or max) — zone 20 */
const FARM_LEGENDARIES: WildPokemon[] = [
  { nameFr: 'Mewtwo', nameEn: 'Mewtwo', slug: 'mewtwo', type: 'psychic', baseHp: 120, baseAtk: 30 },
  { nameFr: 'Rayquaza', nameEn: 'Rayquaza', slug: 'rayquaza', type: 'dragon', baseHp: 120, baseAtk: 30 },
  { nameFr: 'Dialga', nameEn: 'Dialga', slug: 'dialga', type: 'steel', baseHp: 120, baseAtk: 28 },
  { nameFr: 'Palkia', nameEn: 'Palkia', slug: 'palkia', type: 'water', baseHp: 120, baseAtk: 28 },
  { nameFr: 'Giratina', nameEn: 'Giratina', slug: 'giratina', type: 'ghost', baseHp: 120, baseAtk: 28 },
  { nameFr: 'Zacian', nameEn: 'Zacian', slug: 'zacian', type: 'fairy', baseHp: 115, baseAtk: 30 },
  { nameFr: 'Zamazenta', nameEn: 'Zamazenta', slug: 'zamazenta', type: 'fighting', baseHp: 115, baseAtk: 28 },
  { nameFr: 'Koraidon', nameEn: 'Koraidon', slug: 'koraidon', type: 'fighting', baseHp: 115, baseAtk: 30 },
  { nameFr: 'Miraidon', nameEn: 'Miraidon', slug: 'miraidon', type: 'electric', baseHp: 115, baseAtk: 30 },
  { nameFr: 'Solgaleo', nameEn: 'Solgaleo', slug: 'solgaleo', type: 'steel', baseHp: 110, baseAtk: 28 },
  { nameFr: 'Lunala', nameEn: 'Lunala', slug: 'lunala', type: 'ghost', baseHp: 110, baseAtk: 28 },
  { nameFr: 'Kyurem', nameEn: 'Kyurem', slug: 'kyurem', type: 'dragon', baseHp: 115, baseAtk: 28 },
]

export function getBattleTowerFarmZone(): Zone {
  return {
    id: BATTLE_TOWER_FARM_ZONE_INDEX,
    nameFr: 'Zone spéciale — réserve',
    nameEn: 'Special zone — reserve',
    types: ['dragon', 'psychic', 'steel'],
    wild: FARM_LEGENDARIES,
    boss: {
      nameFr: '—',
      nameEn: '—',
      slug: 'placeholder',
      types: ['normal'],
      team: [{ nameFr: '—', nameEn: '—', slug: 'ditto', level: 1 }],
      timerSeconds: 30,
    },
  }
}

export function getBattleTowerZone(zoneIndex: number): Zone | undefined {
  if (zoneIndex === BATTLE_TOWER_FARM_ZONE_INDEX) return getBattleTowerFarmZone()
  if (zoneIndex === BATTLE_TOWER_CAPSTONE_ZONE_INDEX) return getBattleTowerCapstoneZone()
  return getBattleTowerBossZone(zoneIndex)
}

/** Libellé UI : type + numéro d’épreuve (1–18), Red (19), réserve (20). */
export function getBattleTowerProgressLabel(
  zoneIndex: number,
  locale: 'fr' | 'en' = 'fr',
): string {
  if (zoneIndex >= 1 && zoneIndex <= 18) {
    const type = BATTLE_TOWER_TYPE_ORDER[zoneIndex - 1]
    const ti = type ? getTypeInfo(type) : null
    const typeName = locale === 'en' ? ti?.nameEn : ti?.nameFr
    return locale === 'en'
      ? `Tower — ${typeName ?? 'Trial'} · Floor ${zoneIndex}/18`
      : `Tour — ${typeName ?? 'Épreuve'} · ${zoneIndex}/18`
  }
  if (zoneIndex === BATTLE_TOWER_CAPSTONE_ZONE_INDEX) {
    return locale === 'en' ? 'Tower — Red · Capstone' : 'Tour — Red · Capstone'
  }
  if (zoneIndex === BATTLE_TOWER_FARM_ZONE_INDEX) {
    return locale === 'en' ? 'Reserve — sanctuary farm' : 'Réserve — farm sanctuaire'
  }
  return ''
}

export function battleTowerFarmUnlocked(defeatedTypes: PokemonType[]): boolean {
  return defeatedTypes.length >= BATTLE_TOWER_TYPE_ORDER.length
}
